import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  User,
  Lock,
  Save,
  Loader2,
  Bell,
  Shield,
  Trash2,
  LogOut,
  ChevronRight,
  CreditCard,
  Cloud,
  Users,
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

export function SettingsPage() {
  const { user, logout, setUser } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [weeklyReport, setWeeklyReport] = useState(true)
  const [leadAlerts, setLeadAlerts] = useState(true)

  const updateProfileMutation = useMutation({
    mutationFn: (data: { name: string; email: string }) =>
      api.updateProfile(data),
    onSuccess: (data) => {
      if (data?.user) {
        setUser(data.user)
      }
      queryClient.invalidateQueries({ queryKey: ['user'] })
      toast.success('Profile updated!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.changePassword(data),
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast.success('Password changed!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteAccountMutation = useMutation({
    mutationFn: () => api.deleteAccount(),
    onSuccess: () => {
      logout()
      navigate('/')
      toast.success('Account deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault()
    updateProfileMutation.mutate({ name, email })
  }

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    changePasswordMutation.mutate({ currentPassword, newPassword })
  }

  const handleDeleteAccount = () => {
    if (
      confirm(
        'Are you sure you want to delete your account? This action cannot be undone. All your domains, sites, and data will be permanently deleted.'
      )
    ) {
      deleteAccountMutation.mutate()
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    toast.success('Logged out')
  }

  const settingsLinks = [
    {
      name: 'Billing',
      description: 'Manage subscription and payment methods',
      icon: CreditCard,
      href: '/dashboard/settings/billing',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      name: 'Cloudflare',
      description: 'Connect and manage Cloudflare integration',
      icon: Cloud,
      href: '/dashboard/settings/cloudflare',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
    },
    {
      name: 'Team',
      description: 'Assign leads and manage teammates',
      icon: Users,
      href: '/dashboard/settings/team',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
  ]

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 sm:grid-cols-2">
        {settingsLinks.map((link) => (
          <Link
            key={link.name}
            to={link.href}
            className="card-hover flex items-center gap-4"
          >
            <div className={cn('p-3 rounded-xl', link.bgColor)}>
              <link.icon className={cn('h-6 w-6', link.color)} />
            </div>
            <div className="flex-1">
              <p className="font-semibold">{link.name}</p>
              <p className="text-sm text-muted-foreground">{link.description}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </Link>
        ))}
      </div>

      {/* Profile */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <User className="h-5 w-5" />
          Profile
        </h2>

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label className="label mb-1.5 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label mb-1.5 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </div>

          <button
            type="submit"
            disabled={updateProfileMutation.isPending}
            className="btn-primary"
          >
            {updateProfileMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Change Password
        </h2>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="label mb-1.5 block">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label mb-1.5 block">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input"
              minLength={8}
            />
          </div>

          <div>
            <label className="label mb-1.5 block">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              minLength={8}
            />
          </div>

          <button
            type="submit"
            disabled={changePasswordMutation.isPending || !currentPassword || !newPassword}
            className="btn-primary"
          >
            {changePasswordMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Change Password
              </>
            )}
          </button>
        </form>
      </div>

      {/* Notifications */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
        </h2>

        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">
                Receive updates about your account
              </p>
            </div>
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="font-medium">Weekly Report</p>
              <p className="text-sm text-muted-foreground">
                Get a summary of your sites' performance
              </p>
            </div>
            <input
              type="checkbox"
              checked={weeklyReport}
              onChange={(e) => setWeeklyReport(e.target.checked)}
              className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="font-medium">Lead Alerts</p>
              <p className="text-sm text-muted-foreground">
                Get notified when new leads are captured
              </p>
            </div>
            <input
              type="checkbox"
              checked={leadAlerts}
              onChange={(e) => setLeadAlerts(e.target.checked)}
              className="h-5 w-5 rounded border-border text-primary focus:ring-primary"
            />
          </label>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border-destructive/50">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-destructive">
          <Shield className="h-5 w-5" />
          Danger Zone
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <p className="font-medium">Log Out</p>
              <p className="text-sm text-muted-foreground">
                Sign out of your account
              </p>
            </div>
            <button onClick={handleLogout} className="btn-outline">
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <div>
              <p className="font-medium text-destructive">Delete Account</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteAccountMutation.isPending}
              className="btn-outline text-destructive hover:bg-destructive/10"
            >
              {deleteAccountMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
